import React, { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  VStack,
  HStack,
  Button,
  Text,
  Heading,
  Badge,
  useToast,
  useDisclosure,
  SimpleGrid,
  Icon,
  Divider,
  Progress,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton
} from '@chakra-ui/react';
import { 
  FaInfoCircle, 
  FaGift, 
  FaWallet, 
  FaTrophy, 
  FaLock,
  FaPlus,
  FaMinus,
  FaChartLine,
  FaCoins,
  FaTicketAlt,
  FaDice
} from 'react-icons/fa';
import { motion } from 'framer-motion';
import UniversalScratchCard from './CardTypes/UniversalScratchCard';
import { useBalance } from '../../../context/BalanceContext';
import { mergeWithTemplate } from './Templates/UniversalGameTemplate';

// Animation variants
const MotionBox = motion(Box);
const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.5 } }
};

/**
 * UniversalGameComponent
 * 
 * A unified game component that can render any scratch card game
 * based on the provided configuration. This ensures consistency
 * across all game types while allowing for customization.
 */
const UniversalGameComponent = ({ gameConfig }) => {
  // Merge with template to ensure all required properties exist
  // If gameConfig is undefined or null, use an empty object
  const config = mergeWithTemplate(gameConfig || {});
  
  // Extract theme and features
  const { theme, features, card, prizes } = config;
  
  // Get user balance from context
  const { balance, updateBalance } = useBalance();
  
  // Game state
  const [gameState, setGameState] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [cardCount, setCardCount] = useState(1);
  const [userTickets, setUserTickets] = useState(0);
  const [totalTickets, setTotalTickets] = useState(1000);
  const [recentWinners, setRecentWinners] = useState([]);
  
  // Modal states
  const { 
    isOpen: isGiftOpen, 
    onOpen: onGiftOpen, 
    onClose: onGiftClose 
  } = useDisclosure();
  
  const { 
    isOpen: isWinOpen, 
    onOpen: onWinOpen, 
    onClose: onWinClose 
  } = useDisclosure();
  
  const toast = useToast();
  
  // Initialize game
  useEffect(() => {
    // Generate random cell values for demonstration
    const generateGameState = () => {
      const totalCells = card.grid.rows * card.grid.columns;
      const cellValues = Array(totalCells).fill().map(() => {
        // Generate random values (in a real game, these would come from the backend)
        const randomIndex = Math.floor(Math.random() * prizes.tiers.length);
        return prizes.tiers[randomIndex].amount.toString();
      });
      
      // Randomly select winning cells (3 by default)
      const winningCount = config.card.symbols.winningCount || 3;
      const winningCells = [];
      while (winningCells.length < winningCount) {
        const randomCell = Math.floor(Math.random() * totalCells);
        if (!winningCells.includes(randomCell)) {
          winningCells.push(randomCell);
        }
      }
      
      return {
        id: `game-${Date.now()}`,
        status: 'ready', // ready, active, completed
        cellValues,
        winningCells,
        revealedCells: [],
        prizeWon: 0,
        isWinner: false
      };
    };
    
    setGameState(generateGameState());
    
    // Generate random recent winners
    const generateRecentWinners = () => {
      return Array(5).fill().map((_, i) => ({
        id: `winner-${i}`,
        name: `Player${Math.floor(Math.random() * 1000)}`,
        prize: prizes.tiers[Math.floor(Math.random() * prizes.tiers.length)].amount,
        timestamp: new Date(Date.now() - Math.random() * 86400000 * 7)
      }));
    };
    
    setRecentWinners(generateRecentWinners());
    
    // Set random user tickets
    setUserTickets(Math.floor(Math.random() * 50));
    setTotalTickets(1000 + Math.floor(Math.random() * 5000));
  }, [config.id]);
  
  // Handle buying cards
  const handleBuy = () => {
    if (balance < config.cost * cardCount) {
      toast({
        title: "Insufficient balance",
        description: "You don't have enough funds to buy these cards.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    // Deduct balance
    updateBalance(balance - config.cost * cardCount);
    
    // Start game
    setGameState({
      ...gameState,
      status: 'active'
    });
    
    setIsPlaying(true);
    
    // Add lottery tickets if feature is enabled
    if (features.lottery) {
      setUserTickets(prev => prev + cardCount);
      setTotalTickets(prev => prev + cardCount);
    }
    
    toast({
      title: "Cards purchased!",
      description: `You bought ${cardCount} card${cardCount > 1 ? 's' : ''}.`,
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };
  
  // Handle cell reveal
  const handleReveal = (index) => {
    if (!gameState || gameState.status !== 'active') return;
    
    // Update game state
    const newGameState = { ...gameState };
    newGameState.revealedCells = [...newGameState.revealedCells, index];
    
    // Check if winning cell
    if (newGameState.winningCells.includes(index)) {
      const prizeValue = parseInt(newGameState.cellValues[index], 10);
      newGameState.prizeWon += prizeValue;
      newGameState.isWinner = true;
    }
    
    // Check if all cells are revealed
    if (newGameState.revealedCells.length === card.grid.rows * card.grid.columns) {
      newGameState.status = 'completed';
      
      // Add to balance if winner
      if (newGameState.isWinner) {
        updateBalance(balance + newGameState.prizeWon);
        onWinOpen();
      }
    }
    
    setGameState(newGameState);
  };
  
  // Handle reveal all
  const handleRevealAll = () => {
    if (!gameState || gameState.status !== 'active') return;
    
    // Reveal all cells
    const totalCells = card.grid.rows * card.grid.columns;
    const allCells = Array(totalCells).fill().map((_, i) => i);
    
    // Update game state
    const newGameState = { ...gameState };
    newGameState.revealedCells = allCells;
    newGameState.status = 'completed';
    
    // Calculate prize
    newGameState.prizeWon = 0;
    newGameState.winningCells.forEach(cellIndex => {
      const prizeValue = parseInt(newGameState.cellValues[cellIndex], 10);
      newGameState.prizeWon += prizeValue;
    });
    
    newGameState.isWinner = newGameState.prizeWon > 0;
    
    // Add to balance if winner
    if (newGameState.isWinner) {
      updateBalance(balance + newGameState.prizeWon);
      onWinOpen();
    }
    
    setGameState(newGameState);
  };
  
  // Handle gift
  const handleGift = () => {
    onGiftOpen();
  };
  
  // Handle sending gift
  const handleSendGift = (recipient) => {
    if (balance < config.cost) {
      toast({
        title: "Insufficient balance",
        description: "You don't have enough funds to gift this card.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    // Deduct balance
    updateBalance(balance - config.cost);
    
    toast({
      title: "Gift sent!",
      description: `You sent a ${config.title} card to ${recipient}.`,
      status: "success",
      duration: 3000,
      isClosable: true,
    });
    
    onGiftClose();
  };
  
  // Handle lottery spin
  const handleLotterySpin = () => {
    if (userTickets <= 0) {
      toast({
        title: "No tickets",
        description: "You need tickets to participate in the lottery.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    // Simulate lottery spin
    const winChance = userTickets / totalTickets;
    const isWinner = Math.random() < winChance * 0.1; // 10% of theoretical chance
    
    if (isWinner) {
      const prizeIndex = Math.floor(Math.random() * prizes.tiers.length);
      const prizeAmount = prizes.tiers[prizeIndex].amount;
      
      updateBalance(balance + prizeAmount);
      
      toast({
        title: "Congratulations!",
        description: `You won ${prizeAmount} ${prizes.tokenType} in the lottery!`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } else {
      toast({
        title: "Better luck next time",
        description: "You didn't win this time. Try again later!",
        status: "info",
        duration: 3000,
        isClosable: true,
      });
    }
  };
  
  // Increment/decrement card count
  const incrementCardCount = () => setCardCount(prev => Math.min(prev + 1, 10));
  const decrementCardCount = () => setCardCount(prev => Math.max(prev - 1, 1));
  
  // Calculate user's lottery chance
  const userLotteryChance = (userTickets / totalTickets) * 100;
  
  // Calculate total prize pool
  const totalPrizePool = prizes.tiers.reduce((total, tier) => {
    return total + (tier.amount * tier.count);
  }, 0);
  
  return (
    <Box width="100%" minHeight="auto" p={4}>
      <Flex direction={{ base: 'column', md: 'row' }} width="100%" gap={4}>
        {/* Left Column - Card */}
        <Box 
          width={config.layout.cardColumn.width}
          display="flex"
          justifyContent="center"
        >
          <MotionBox
            initial="hidden"
            animate="visible"
            variants={fadeIn}
          >
            <UniversalScratchCard 
              config={config}
              gameState={gameState}
              onReveal={handleReveal}
              isPreview={false}
            />
          </MotionBox>
        </Box>
        
        {/* Right Column - Controls and Info */}
        <VStack 
          width={{ base: "100%", md: "450px" }}
          spacing={6}
          align="stretch"
        >
          {/* Game Controls */}
          <Box
            bg={theme.colors.secondary}
            borderRadius="lg"
            p={5}
            boxShadow={theme.glassmorphism.boxShadow}
            border={theme.glassmorphism.border}
            backdropFilter={theme.glassmorphism.backdropFilter}
          >
            <Heading size="md" mb={4}>Game Controls</Heading>
            
            {!isPlaying ? (
              <>
                <HStack justify="space-between" mb={4}>
                  <Text>Price per Card:</Text>
                  <Text fontWeight="bold">{config.cost} SOL</Text>
                </HStack>
                
                <HStack justify="space-between" mb={4}>
                  <Text>Cards:</Text>
                  <HStack>
                    <Button 
                      size="sm" 
                      onClick={decrementCardCount} 
                      isDisabled={cardCount <= 1}
                      colorScheme="red"
                    >
                      <Icon as={FaMinus} />
                    </Button>
                    <Text fontWeight="bold" mx={2}>{cardCount}</Text>
                    <Button 
                      size="sm" 
                      onClick={incrementCardCount} 
                      isDisabled={cardCount >= 10}
                      colorScheme="green"
                    >
                      <Icon as={FaPlus} />
                    </Button>
                  </HStack>
                </HStack>
                
                <HStack justify="space-between" mb={4}>
                  <Text>Total Cost:</Text>
                  <Text fontWeight="bold">{(config.cost * cardCount).toFixed(2)} SOL</Text>
                </HStack>
                
                <HStack justify="space-between" mb={4}>
                  <Text>Your Balance:</Text>
                  <Text fontWeight="bold">{balance.toFixed(2)} SOL</Text>
                </HStack>
                
                <Button 
                  colorScheme="green" 
                  width="100%" 
                  onClick={handleBuy}
                  isDisabled={balance < config.cost * cardCount}
                >
                  Buy Now
                </Button>
                
                {features.giftOption && (
                  <Button 
                    colorScheme="purple" 
                    width="100%" 
                    mt={2} 
                    leftIcon={<FaGift />}
                    onClick={handleGift}
                    isDisabled={balance < config.cost}
                  >
                    Gift to Friend
                  </Button>
                )}
              </>
            ) : (
              <>
                <Text mb={4}>
                  Scratch the card to reveal symbols. Match 3 of the same symbols to win!
                </Text>
                
                {features.revealAll && (
                  <Button 
                    colorScheme="blue" 
                    width="100%" 
                    onClick={handleRevealAll}
                    isDisabled={gameState?.status !== 'active'}
                  >
                    Reveal All
                  </Button>
                )}
                
                <Button 
                  colorScheme="green" 
                  width="100%" 
                  mt={2} 
                  onClick={() => {
                    setIsPlaying(false);
                    setGameState({
                      ...gameState,
                      status: 'ready',
                      revealedCells: [],
                      prizeWon: 0,
                      isWinner: false
                    });
                  }}
                  isDisabled={gameState?.status === 'active'}
                >
                  Play Again
                </Button>
              </>
            )}
          </Box>
          
          {/* Bounty Pool */}
          {features.bountyPool && (
            <Box
              bg={theme.colors.secondary}
              borderRadius="lg"
              p={5}
              boxShadow={theme.glassmorphism.boxShadow}
              border={theme.glassmorphism.border}
              backdropFilter={theme.glassmorphism.backdropFilter}
            >
              <Heading size="md" mb={4}>Bounty Pool</Heading>
              
              <Stat mb={4}>
                <StatLabel>Total Prize Pool</StatLabel>
                <StatNumber>{totalPrizePool.toLocaleString()} {prizes.tokenType}</StatNumber>
                <StatHelpText>
                  <Badge colorScheme="green">Active</Badge>
                </StatHelpText>
              </Stat>
              
              <Divider my={4} />
              
              <Text fontWeight="bold" mb={2}>Prize Tiers</Text>
              <VStack align="stretch" spacing={2}>
                {prizes.tiers.map((tier, index) => (
                  <HStack key={index} justify="space-between">
                    <Text>{tier.amount.toLocaleString()} {prizes.tokenType}</Text>
                    <Badge colorScheme="blue">{tier.count} remaining</Badge>
                  </HStack>
                ))}
              </VStack>
            </Box>
          )}
          
          {/* Lottery Feature */}
          {features.lottery && (
            <Box
              bg={theme.colors.secondary}
              borderRadius="lg"
              p={5}
              boxShadow={theme.glassmorphism.boxShadow}
              border={theme.glassmorphism.border}
              backdropFilter={theme.glassmorphism.backdropFilter}
            >
              <Heading size="md" mb={4}>Lottery Draw</Heading>
              
              <HStack justify="space-between" mb={2}>
                <Text>Your Tickets:</Text>
                <Badge colorScheme="purple" p={2}>{userTickets}</Badge>
              </HStack>
              
              <Text fontSize="sm" mb={2}>Win Chance: {userLotteryChance.toFixed(2)}%</Text>
              
              <Progress 
                value={userLotteryChance} 
                colorScheme="purple" 
                size="sm" 
                borderRadius="md" 
                mb={4} 
              />
              
              <Button 
                colorScheme="purple" 
                width="100%" 
                leftIcon={<FaDice />}
                onClick={handleLotterySpin}
                isDisabled={userTickets <= 0}
              >
                Spin the Wheel
              </Button>
              
              <Text fontSize="xs" mt={2} textAlign="center">
                Buy cards to earn lottery tickets!
              </Text>
            </Box>
          )}
          
          {/* Recent Winners */}
          {features.recentWinners && (
            <Box
              bg={theme.colors.secondary}
              borderRadius="lg"
              p={5}
              boxShadow={theme.glassmorphism.boxShadow}
              border={theme.glassmorphism.border}
              backdropFilter={theme.glassmorphism.backdropFilter}
            >
              <Heading size="md" mb={4}>Recent Winners</Heading>
              
              <VStack align="stretch" spacing={2}>
                {recentWinners.map((winner) => (
                  <HStack key={winner.id} justify="space-between" p={2} borderRadius="md" bg="rgba(0,0,0,0.2)">
                    <Text fontSize="sm">{winner.name}</Text>
                    <HStack>
                      <Text fontSize="sm" fontWeight="bold">{winner.prize.toLocaleString()} {prizes.tokenType}</Text>
                      <Badge colorScheme="green" size="sm">
                        {new Date(winner.timestamp).toLocaleDateString()}
                      </Badge>
                    </HStack>
                  </HStack>
                ))}
              </VStack>
            </Box>
          )}
        </VStack>
      </Flex>
      
      {/* Gift Modal */}
      <Modal isOpen={isGiftOpen} onClose={onGiftClose}>
        <ModalOverlay />
        <ModalContent bg={theme.colors.secondary} color={theme.colors.text}>
          <ModalHeader>Gift a Card</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb={4}>Enter the username or wallet address of the recipient:</Text>
            <Box
              as="input"
              p={2}
              borderRadius="md"
              width="100%"
              bg="rgba(0,0,0,0.2)"
              border="1px solid"
              borderColor="rgba(255,255,255,0.1)"
              color="white"
              _focus={{ borderColor: theme.colors.accent }}
              placeholder="Username or wallet address"
            />
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onGiftClose}>
              Cancel
            </Button>
            <Button colorScheme="purple" onClick={() => handleSendGift("Friend")}>
              Send Gift
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      
      {/* Win Modal */}
      <Modal isOpen={isWinOpen} onClose={onWinClose}>
        <ModalOverlay />
        <ModalContent bg={theme.colors.secondary} color={theme.colors.text}>
          <ModalHeader>Congratulations!</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <Icon as={FaTrophy} boxSize={16} color="yellow.400" />
              <Heading size="md">You Won!</Heading>
              <Text fontSize="2xl" fontWeight="bold">
                {gameState?.prizeWon.toLocaleString()} {prizes.tokenType}
              </Text>
              <Text>The prize has been added to your balance.</Text>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="green" mr={3} onClick={onWinClose}>
              Awesome!
            </Button>
            <Button variant="ghost" onClick={() => {
              onWinClose();
              setIsPlaying(false);
              setGameState({
                ...gameState,
                status: 'ready',
                revealedCells: [],
                prizeWon: 0,
                isWinner: false
              });
            }}>
              Play Again
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default UniversalGameComponent;
